import React, { useEffect, useMemo, useState } from 'react'
import {
	Box, Button, Chip, IconButton,
	TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { IconSeeker } from '@ui-seeker/core'
import { AppAntSwitch, AppBulkEditAction, AppDialogCancel, AppFilterChip, AppFormDrawer, AppNote, secondaryButtonSx } from '@components'
import { dsColorNew } from '@palette'
import { Close as CloseIcon, CalendarMonth as CalendarMonthIcon, Schedule as ScheduleIcon, FlightTakeoff as FlightTakeoffIcon, FlightLand as FlightLandIcon, Event as EventIcon, Store as StoreIcon, Bolt as BoltIcon, Add as AddIcon } from 'Pages/DsRepricing/@components/icons'
import OfficeScopePicker from 'Pages/DsRepricing/@components/OfficeScopePicker'
import Card from 'Pages/DsRepricing/@components/SectionCard'
import DateRangeField from 'Pages/DsRepricing/@components/DateRangeField'
import { futurePresets } from 'Pages/DsRepricing/@services/dateRange'
import { hhmm } from 'Pages/DsRepricing/Schedule/@services/adapters'
import { ruleHoursForDay, WEEKDAY_LABELS } from '../mockData'
import { tonePillSx, toneChipSx } from 'Pages/DsRepricing/@components/tones'

// Pickup and dropoff are clock times a rate shop is launched at, and
// nobody schedules one at 09:07. `step` is in seconds: the native
// picker then moves in half hours and refuses anything off the grid,
// which also stops a typo reaching the sweep.
const HALF_HOUR = { step: 1800 }

const WEEKDAYS = ['mon','tue','wed','thu','fri','sat','sun']
const WEEKDAY_PRESETS = [
	{ label: 'All',      days: [...WEEKDAYS] },
	{ label: 'Weekdays', days: ['mon','tue','wed','thu','fri'] },
	{ label: 'Weekend',  days: ['sat','sun'] },
]

const EMPTY = {
	name: '', is_active: true, priority: 50,
	mode: 'rolling',
	dba_from: 0, dba_to: 7,
	fixed_from: '', fixed_to: '',
	frequency_kind: 'every_n_hours', frequency_value: 1,
	window_start: '06:00', window_end: '22:00',
	weekdays: [...WEEKDAYS],
	office_codes: [],
	pickup_hour: null, dropoff_hour: null,
}

export default function Drawer({ open, rule, allRules, onClose, onSave, onError, error = null }) {
	const [draft, setDraft] = useState(EMPTY)

	useEffect(() => {
		if (rule && rule.id) setDraft({ ...EMPTY, ...rule })
		else setDraft(EMPTY)
	}, [rule])

	const previewRuns = useMemo(() => {
		try { return ruleHoursForDay(draft) } catch { return [] }
	}, [draft])

	const set = (patch) => setDraft(d => ({ ...d, ...patch }))

	const save = () => {
		onError(null)
		if (!draft.name.trim()) return onError('Name is required.')
		if (draft.mode === 'rolling') {
			if (draft.dba_from > draft.dba_to)
				return onError('DBA "from" cannot be greater than "to".')
		} else {
			if (!draft.fixed_from || !draft.fixed_to)
				return onError('Fixed dates are required.')
			if (draft.fixed_from > draft.fixed_to)
				return onError('"From" date is later than "to" date.')
		}
		// The adapter drops seconds and falls back on an empty box; what
		// it cannot read is a half-typed time, which is better said here
		// than as the server's regex.
		const [ws, we] = [hhmm(draft.window_start), hhmm(draft.window_end)]
		if ((draft.window_start && !ws) || (draft.window_end && !we))
			return onError('The run window needs two complete times (HH:MM).')
		if (ws && we && ws >= we)
			return onError('The run window must start before it ends.')
		if (draft.weekdays.length === 0)
			return onError('Select at least one weekday.')
		if (draft.frequency_kind === 'specific_hours' && !(draft.frequency_value || []).length)
			return onError('Add at least one specific hour.')
		onSave({ id: rule?.id, ...draft })
	}

	return (
		<AppFormDrawer isOpen={open} controller={onClose}
		               width={{ xs: '100%', sm: 640, md: 760 }}
		               containerProps={{ PaperProps: { sx: setupScopeSx } }}
		               icon="clock"
		               title={rule?.id ? 'Edit schedule rule' : 'New schedule rule'}
		               subtitle={<>Each rule decides <em>when</em> the repricing engine runs
		                   for a given pickup range and scope.</>}
		               headerAside={
			               // The rule's Live / Paused switch beside the ×
			               <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mr: 1 }}>
				               <Box component="span" sx={tonePillSx(draft.is_active ? 'ok' : 'neutral')}>
					               {draft.is_active ? 'Live' : 'Paused'}
				               </Box>
				               <AppAntSwitch checked={draft.is_active}
				                             onChange={e => set({ is_active: e.target.checked })}/>
			               </Box>
		               }
		               footer={<>
			               {/* Why the save was refused goes next to the Save button.
			                   It used to land on the list behind the drawer, where the
			                   operator could not read it (Curro, 2026-10-02). */}
			               {error && (
				               <AppNote severity="error" sx={{ flex: 1, mr: 1 }}>
					               {error}
				               </AppNote>
			               )}
			               <AppDialogCancel onClick={onClose}/>
			               <AppBulkEditAction startIcon={<IconSeeker icon="floppy-disk"/>} onClick={save}>
				               Save
			               </AppBulkEditAction>
		               </>}>
			{/* Identity */}
			<Card step={1} title="Identity"
			      subtitle="How you recognise the rule in the list and how much weight it carries against other rules.">
				<Box display="flex" gap={2} flexWrap="wrap">
					<TextField label="Name" value={draft.name} size="small"
					           onChange={e => set({ name: e.target.value })}
					           placeholder="e.g. Rally week"
					           autoFocus sx={{ flex: 1, minWidth: 240 }}/>
					<TextField label="Priority" type="number" size="small"
					           value={draft.priority}
					           onChange={e => set({ priority: Number(e.target.value) })}
					           helperText="Lower wins on overlaps"
					           sx={{ width: 150 }}/>
				</Box>
			</Card>

			{/* When it applies */}
			<Card step={2} title="When does it apply?"
			      subtitle="Which pickup dates it covers. Rolling moves with today; Fixed uses absolute dates for one-off events."
			      icon={<CalendarMonthIcon/>}>
				<ChoiceStrip
					value={draft.mode}
					onChange={v => set({ mode: v })}
					options={[
						{ value: 'rolling', label: 'Rolling',
						  hint: 'Relative to today (DBA)' },
						{ value: 'fixed',   label: 'Fixed',
						  hint: 'Absolute dates' },
					]}/>
				<Box sx={{ mt: 2 }}>
					{draft.mode === 'rolling' ? (
						<Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
							<TextField size="small" type="number" label="From (DBA)"
							           value={draft.dba_from}
							           onChange={e => set({ dba_from: Number(e.target.value) })}
							           sx={{ width: 150 }}/>
							<Box sx={{ color: dsColorNew.textSubtle, fontSize: 16 }}>—</Box>
							<TextField size="small" type="number" label="To (DBA)"
							           value={draft.dba_to}
							           onChange={e => set({ dba_to: Number(e.target.value) })}
							           sx={{ width: 150 }}/>
							<Typography sx={{ color: dsColorNew.textMuted,
							                  fontSize: 12, ml: 1 }}>
								DBA = days before arrival
							</Typography>
						</Box>
					) : (
						<DateRangeField from={draft.fixed_from} to={draft.fixed_to}
						                placeholder="Pick the first and last day"
						                presets={futurePresets()} width={320}
						                onChange={({ from, to }) =>
						                    set({ fixed_from: from || '', fixed_to: to || '' })}/>
					)}
				</Box>
			</Card>

			{/* Frequency */}
			<Card step={3} title="Frequency"
			      subtitle="How often the engine fires within an active day."
			      icon={<ScheduleIcon/>}>
				<ChoiceStrip
					value={draft.frequency_kind}
					onChange={v => set({ frequency_kind: v,
					                     frequency_value: v === 'specific_hours' ? [] : 3 })}
					options={[
						{ value: 'every_n_hours',  label: 'Every N hours',
						  hint: 'Regular cadence' },
						{ value: 'specific_hours', label: 'Specific hours',
						  hint: 'Literal list' },
					]}/>
				<Box sx={{ mt: 2 }}>
					{draft.frequency_kind === 'every_n_hours' ? (
						<Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
							<TextField size="small" type="number" label="Every"
							           value={draft.frequency_value}
							           onChange={e => set({ frequency_value: Number(e.target.value) })}
							           InputProps={{ endAdornment:
							               <Typography sx={{ color: dsColorNew.textMuted,
							                                 fontSize: 12, ml: 0.5 }}>
							                   hours
							               </Typography> }}
							           sx={{ width: 180 }}/>
							<Box display="flex" gap={1} alignItems="center">
								<TextField size="small" type="time" inputProps={HALF_HOUR} label="Window from"
								           value={draft.window_start}
								           onChange={e => set({ window_start: e.target.value })}
								           InputLabelProps={{ shrink: true }}
								           sx={{ width: 150 }}/>
								<Box sx={{ color: dsColorNew.textSubtle }}>—</Box>
								<TextField size="small" type="time" inputProps={HALF_HOUR} label="to"
								           value={draft.window_end}
								           onChange={e => set({ window_end: e.target.value })}
								           InputLabelProps={{ shrink: true }}
								           sx={{ width: 130 }}/>
							</Box>
						</Box>
					) : (
						<SpecificHoursEditor
							value={draft.frequency_value || []}
							onChange={v => set({ frequency_value: v })}/>
					)}
				</Box>
			</Card>

			{/* Weekdays */}
			<Card step={4} title="Weekdays"
			      subtitle="On which days of the week the rule may fire."
			      icon={<EventIcon/>}>
				<ToggleButtonGroup value={draft.weekdays} size="small"
				                   onChange={(_, v) => v && set({ weekdays: v })}>
					{WEEKDAYS.map(d => (
						<ToggleButton key={d} value={d}
						              sx={{ minWidth: 44 }}>
							{WEEKDAY_LABELS[d]}
						</ToggleButton>
					))}
				</ToggleButtonGroup>
				<Box display="flex" gap={0.5} sx={{ mt: 1.25 }}>
					{WEEKDAY_PRESETS.map(p => (
						<AppFilterChip key={p.label} label={p.label} sx={{ height: 28 }}
						               selected={[...draft.weekdays].sort().join() === [...p.days].sort().join()}
						               onClick={() => set({ weekdays: p.days })}/>
					))}
				</Box>
			</Card>

			{/* Pickup / dropoff combination */}
			<Card step={5} title="Pickup / dropoff combination"
			      subtitle="Filter the rate shop by pickup and dropoff time. Leave blank for no filter."
			      icon={<FlightTakeoffIcon/>}>
				<Box display="flex" gap={2} flexWrap="wrap">
					<HourPickerCard label="Pickup"
					                icon={<FlightTakeoffIcon fontSize="small"/>}
					                value={draft.pickup_hour}
					                onChange={v => set({ pickup_hour: v })}/>
					<HourPickerCard label="Dropoff"
					                icon={<FlightLandIcon fontSize="small"/>}
					                value={draft.dropoff_hour}
					                onChange={v => set({ dropoff_hour: v })}/>
				</Box>
			</Card>

			{/* Office scope */}
			<Card step={6} title="Office scope"
			      subtitle="Which offices it reaches. Empty = all offices in the tenant. ACRISS filtering is handled by each strategy."
			      icon={<StoreIcon/>}>
				{/* `requireMapped`: this rule launches searches, and a
				    search is addressed by Price Seeker id. An office
				    without one produces nothing, and the validator
				    refuses it on save — so it is offered struck
				    through, with the screen that fixes it, instead of
				    being offered as if it worked. */}
				<OfficeScopePicker value={draft.office_codes}
				                   onChange={v => set({ office_codes: v })}
				                   requireMapped
				                   label="Offices and office groups"/>
			</Card>

			{/* Preview */}
			<Box sx={{ p: 2.5, borderRadius: 2,
			           bgcolor: dsColorNew.pillActiveBg,
			           border: '1px solid', borderColor: dsColorNew.noteInfoBorder }}>
				<Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
					<BoltIcon sx={{ color: dsColorNew.appBlue }}/>
					<Typography sx={{ fontWeight: 700, fontSize: 13,
					                  color: dsColorNew.appBlue }}>
						Preview
					</Typography>
				</Box>
				<Typography sx={{ fontSize: 13, color: dsColorNew.textTitle }}>
					This rule would fire{' '}
					<Box component="span" sx={{ fontWeight: 700,
					                            color: dsColorNew.appBlue }}>
						{previewRuns.length}
					</Box>{' '}
					repricings on an active day.
				</Typography>
				{previewRuns.length > 0 && (
					<Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mt: 1.25 }}>
						{previewRuns.map(h => (
							<Chip key={h} label={h} size="small"
							      sx={{ height: 24, fontSize: 12,
							            fontWeight: 500, borderRadius: '999px',
							            bgcolor: dsColorNew.white,
							            color: dsColorNew.appBlue,
							            border: '1px solid',
							            borderColor: dsColorNew.noteInfoBorder }}/>
						))}
					</Box>
				)}
			</Box>
		</AppFormDrawer>
	)
}

// ─── Two-option segmented control with hint text ───────────────────────
function ChoiceStrip({ value, onChange, options }) {
	return (
		<Box sx={{ display: 'grid',
		           gridTemplateColumns: `repeat(${options.length}, 1fr)`,
		           gap: 1 }}>
			{options.map(opt => {
				const active = value === opt.value
				return (
					<Box key={opt.value}
					     onClick={() => onChange(opt.value)}
					     sx={{
						cursor: 'pointer', p: 1.5, borderRadius: 1.5,
						border: '2px solid',
						borderColor: active ? dsColorNew.appBlue : dsColorNew.grayBorder,
						bgcolor: active ? dsColorNew.pillActiveBg : dsColorNew.white,
						transition: 'all 0.15s ease',
						'&:hover': { borderColor: active ? dsColorNew.appBlue : dsColorNew.noteInfoBorder },
					}}>
						<Typography sx={{ fontSize: 13, fontWeight: 700,
						                  color: active ? dsColorNew.appBlue : dsColorNew.textTitle }}>
							{opt.label}
						</Typography>
						<Typography sx={{ fontSize: 12,
						                  color: active ? dsColorNew.appBlue : dsColorNew.textMuted }}>
							{opt.hint}
						</Typography>
					</Box>
				)
			})}
		</Box>
	)
}

// ─── Single hour picker (Pickup / Dropoff) ─────────────────────────────
function HourPickerCard({ label, icon, value, onChange }) {
	return (
		<Box sx={{ flex: 1, minWidth: 200, p: 1.5, borderRadius: 1.5,
		           border: '1px solid', borderColor: dsColorNew.grayBorder,
		           bgcolor: dsColorNew.tableRowAlt }}>
			<Box display="flex" alignItems="center" gap={0.75} sx={{ mb: 1 }}>
				<Box sx={{ color: dsColorNew.appBlue, display: 'flex' }}>{icon}</Box>
				<Typography sx={{ fontSize: 12, fontWeight: 700,
				                  textTransform: 'uppercase',
				                  letterSpacing: '0.05em',
				                  color: dsColorNew.textTitle }}>
					{label}
				</Typography>
			</Box>
			<Box display="flex" gap={0.5} alignItems="center">
				<TextField size="small" type="time" inputProps={HALF_HOUR}
				           value={value || ''}
				           onChange={e => onChange(e.target.value || null)}
				           sx={{ flex: 1, bgcolor: dsColorNew.white }}/>
				{value && (
					<IconButton size="small" onClick={() => onChange(null)}
					            aria-label={`Clear ${label}`}>
						<CloseIcon fontSize="small"/>
					</IconButton>
				)}
			</Box>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted,
			                  mt: 0.5 }}>
				{value ? `Only rate shops with ${label.toLowerCase()} at ${value}`
				       : 'Any time'}
			</Typography>
		</Box>
	)
}

// ─── Multi-hour editor for `specific_hours` frequency ─────────────────
function SpecificHoursEditor({ value, onChange }) {
	const [newHour, setNewHour] = useState('09:00')
	const add = () => {
		if (!/^\d\d:\d\d$/.test(newHour) || value.includes(newHour)) return
		onChange([...value, newHour].sort())
	}
	return (
		<Box>
			<Box display="flex" gap={0.75} flexWrap="wrap"
			     sx={{ mb: 1.25, minHeight: 32 }}>
				{value.length === 0 ? (
					<Typography color={dsColorNew.textMuted} sx={{ fontSize: 12,
					                                         fontStyle: 'italic' }}>
						No hours yet. Add at least one.
					</Typography>
				) : value.map(h => (
					<Chip key={h} label={h} size="small"
					      onDelete={() => onChange(value.filter(x => x !== h))}
					      sx={{ ...toneChipSx('info'), '& .MuiChip-deleteIcon': { color: dsColorNew.appBlue, fontSize: 16 } }}/>
				))}
			</Box>
			<Box display="flex" gap={1} alignItems="center">
				<TextField size="small" type="time" inputProps={HALF_HOUR} value={newHour}
				           onChange={e => setNewHour(e.target.value)}
				           sx={{ width: 140 }}/>
				<Button size="small" variant="outlined" onClick={add}
				        startIcon={<AddIcon/>} sx={{ ...secondaryButtonSx, height: 36 }}>
					Add hour
				</Button>
			</Box>
		</Box>
	)
}
